// @flow
/**
 * The main interface for editing a component (widget). This is
 * essentially a UI for ultimately creating a props object.
 */
import React from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import AceEditor from 'react-ace';
import Modal from '~/app/components/Modal';
import Checkbox from '~/pages/components/PageEditor/Checkbox';
import Widgets from 'shared/widgets';
import { S, A, DARK } from '~/app/ui/tokens';

import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-monokai'
import Button from '~/app/ui/Button';

type Props = {
  classes: Object,
  isFetching: boolean,
  preset: Array,
  onClose: () => void,
};

class PreviewPreset extends React.Component {
  props: Props = this.props;
  state = {
    enableDarkTheme: false
  };

  render() {
    if (this.props.isFetching) return <span>Loading...</span>;
    const { classes, preset } = this.props;
    const { enableDarkTheme } = this.state;
    const Component = Widgets[preset.widgetId];
    const footer = (
      <div className={classes.footer}>
        <div className={classes.footerLeft} />
        <div className={classes.footerRight}>
          <Button onClick={this.props.onClose}>Close</Button>
        </div>
      </div>
    );
    const content = (
      <div className={classes.bodyRight}>
        {preset.title}
        <div className={classes.titleLine}>
          <h3 className={classes.bodyRightTitle}>Preview</h3>
          <Checkbox
            value={enableDarkTheme}
            onChange={() => this.setState({ enableDarkTheme: !enableDarkTheme })}
            text="Dark Mode"
          />
        </div>
        <div className={classNames(classes.previewWrapper, enableDarkTheme && classes.darkMode, 'component-browser-wrapper')} data-gx-theme="light">
          <Component {...preset.props} />
        </div>
        <h3>Setting details</h3>
        <AceEditor
          mode="json"
          theme="monokai"
          name="HTML_EDITOR"
          readOnly
          editorProps={{ $blockScrolling: true }}
          value={JSON.stringify(preset.props, null, '\t')}
          width="100%"
          height="500px"
        />
      </div>
    );

    return (
      <Modal
        title={`Preset Preview`}
        actions={footer}
        onClose={this.props.onClose}
      >
        {content}
      </Modal>
    );
  }
}

/* =============================================================================
=    CSS
============================================================================= */
const styles = {
  bg: {
    position: 'fixed',
    top: '49px',
    left: '0',
    bottom: '0',
    right: '0',
    background: 'hsla(0, 0%, 0%, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10
  },
  container: {
    width: '80%',
    height: '80%',
    background: 'hsl(0, 0%, 100%)',
    zIndex: 11,
    padding: '0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  header: {
    flex: '0 0 50px',
    height: '50px',
    color: 'hsl(0, 0%, 90%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 15px',
    background: 'hsl(0, 0%, 10%)'
  },
  body: {
    flexGrow: '2',
    display: 'flex'
  },
  bodyLeft: {
    borderRight: '1px solid hsl(0, 0%, 90%)',
    width: '200px',
    display: 'flex',
    flexDirection: 'column',
    padding: '0',
    background: 'hsl(220, 0%, 95%)'
  },
  menuItem: {
    color: 'hsl(0, 0%, 30%)',
    cursor: 'pointer',
    padding: '15px 10px',
    textAlign: 'left',
    fontWeight: '300',
    background: 'none',
    '&:hover': {
      background: 'hsl(225, 5%, 90%)',
    },
    '&.is-active': {
      color: 'hsl(200, 41%, 33%)',
      fontWeight: '400',
      background: 'hsl(225, 5%, 90%)',
    }
  },
  bodyRight: {
    padding: '20px',
    flexGrow: 2,
    overflowY: 'scroll',
    backgroundColor: 'rgba(0,0,0,0.02)'
  },
  close: {
    fontSize: '15px',
    color: 'hsl(0, 0%, 30%)',
    cursor: 'pointer'
  },
  footer: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 15px',
    borderTop: '1px solid hsl(0, 0%, 90%)',
    [DARK]: { borderTopColor: S.line }
  },
  footerRight: {
    display: 'flex',
    alignItems: 'center'
  },
  titleLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  bodyRightTitle: {
    margin: 0,
    marginTop: '20px'
  },
  darkMode: {
    backgroundColor: 'black'
  },
  /* Mega menus / drawers overflow the preview; stack above the JSON editor below */
  previewWrapper: {
    position: 'relative',
    zIndex: 2,
    overflow: 'visible',
    // Light-scoped (data-gx-theme="light"): the site's white behind the preview, unless it shows the dark mode.
    [DARK]: { background: A.surface, '&$darkMode': { backgroundColor: 'black' } }
  }
};


export default injectSheet(styles)(PreviewPreset);
